import React, { Fragment, useEffect, useRef, useState } from 'react'
import NavigationLanguageSelector from "./NavigationLanguageSelector";

const isMuurikka = document.body.classList.contains('site-muurikka');

/**
 * @typedef {Object} ContentLinkModel
 * @property {Object.<string, string>} attributes - A dictionary of attributes with string keys and values.
 * @property {string} name - The name of the content link.
 * @property {boolean} isMegamenu - Flag indicating if this is part of a megamenu.
 * @property {string} url - The URL of the content link.
 * @property {boolean} disabled - Flag indicating if the link is disabled.
 * @property {boolean} isSelected - Flag indicating if the link is currently selected.
 * @property {ContentLinkModel[]} links - An array of child links.
 * @property {string} extraInfo - Additional information about the link.
 * @property {bool} showSubLinksMobile - Flag indicating whether to show sub-links directly, without having to open a "dropdown".
 * @property {bool} overrideLevel - Override navigation level, mostly used to control classes, such as .navbar__menu.navbar--level-{x}
 * @property {boolean} hasWidget - Computed property indicating if a widget is associated.
 * @property {WidgetItem} widgetItem - The widget item associated with this link.
 */

/**
 * @typedef {Object} WidgetItem
 * @property {string} heading - The heading for the collage item.
 * @property {string} linkText - The text displayed for the link.
 * @property {string} linkUrl - The URL the link points to.
 * @property {string} linkStyle - The style applied to the link.
 * @property {boolean} overlay - Flag indicating if an overlay is used.
 * @property {string} widgetId - The ID of the widget.
 */


/**
 * @param {WidgetItem} widgetItem
 */
const WidgetItem = function ({ widget, className = 'collage-v2-block collage-v2-block--widget' }) {
    if (!widget) {
        return null;
    }

    let node = document.querySelector(`template#${widget.widgetId}`);
    if (!node) {
        return null;
    }

    return <div className={className} dangerouslySetInnerHTML={{ __html: node.innerHTML }}></div>
}

const MenuItem = function ({
    contentLink,
    links,
    showTopLevelIcons,
    hasChildrenClass,
    additionClass,
    megaMenuLink,
    linkHover,
    toggleMenu
}) {
    const [open, setOpen] = useState(false);

    const onClick = (e) => {
        if (links.length) {
            e.preventDefault();
        }

        if (window.innerWidth >= 1024) {
            linkHover(e);
        } else {
            toggleMenu(e);
        }

        setOpen(!open);
    }

    const icon = open ? '-' : '+';

    return <>
        <a
            className={`navbar__link ${!contentLink.url ? 'navbar__link--heading' : ''} ${hasChildrenClass || ''} ${additionClass || ''}`.trim()}
            href={contentLink.url || '#'}
            ref={megaMenuLink} // probably not needed
            onClick={onClick}
        >
            {contentLink.name} {showTopLevelIcons && <span className="d-none large:d-inline">{icon}</span>}
        </a>
        {links.length > 0 &&
            <>
                <i
                    className="navbar__icon--caret navbar__icon navbar__icon--open"
                    onClick={toggleMenu}
                />
            </>
        }
    </>
}

/**
 * @param {ContentLinkModel[]} links
 * @param linkToOtherSites
 * @param {ContentLinkModel} contentLink
 * @param isSubMenu
 * @param {boolean} showWidget
 * @param currentLevel
 * @param {string} logo
 * @param {string} altLogo
 * @param {MutableRefObject} menuRef
 * @param {boolean} isMobile
 * @returns {JSX.Element}
 * @constructor
 */
const Navigation = ({
    links = [],
    contentLink = null,
    isSubMenu = false,
    showWidget = false,
    currentLevel = 0,
    logo = null,
    altLogo = null,
    menuRef = null,
    isMobile
}) => {
    if (!menuRef) {
        menuRef = useRef();
    }

    let burgerBtn = useRef();
    let megaMenuLink = useRef();

    const toggleMenu = () => {
        menuRef.current.classList.toggle('navbar__menu--show')

        if (burgerBtn.current) {
            burgerBtn.current.classList.toggle('navbar__icon--menu')
            burgerBtn.current.classList.toggle('navbar__icon--close')
        }
    }

    const MenuSectionContent = () => {
        let linksToRender = links;
        if (currentLevel === 1 && isMobile) {
            linksToRender = [];

            for (let i = 0; i < links.length; i++) {
                let link = links[i];
                if (link.showSubLinksMobile) {
                    link.links.forEach(subLink => {
                        subLink.overrideLevel = 2;
                        linksToRender.push(subLink);
                    })
                } else {
                    linksToRender.push(link);
                }
            }
        }

        return <>
            <div className="navbar__menu-header">
                {!contentLink ?
                    <div className="navbar__logo">
                        <a href="">
                            <img src={altLogo} />
                        </a>
                    </div>
                    :
                    <>
                        <i
                            className="navbar__icon--caret-left navbar__icon"
                            onClick={toggleMenu}
                        />
                        <span
                            className="navbar__title"
                            onClick={toggleMenu}
                            dangerouslySetInnerHTML={{ __html: contentLink.name }}
                        />
                    </>
                }
            </div>

            {linksToRender.length > 0 &&
                linksToRender.map((link, i) =>
                    !link.name ? null :
                        <Fragment key={`${link.name}-${i}`}>
                            <div className={navbarItemClass(link)}>
                                <Navigation
                                    links={link.links}
                                    contentLink={link}
                                    isSubMenu={true}
                                    showWidget={link && link.hasWidget}
                                    currentLevel={link.overrideLevel || currentLevel + 1}
                                    {...{ logo, altLogo, isMobile }}
                                />
                            </div>
                        </Fragment>
                )}

            <NavigationLanguageSelector />
        </>
    }

    const linkHover = (e) => {
        if (megaMenuLink.current.classList.contains('has-children')) {
            e.preventDefault()

            let header = document.querySelector('header.header');
            let shouldClose = e.target.parentNode.classList.contains('navbar__link--expand');

            document.querySelectorAll('.navbar__item').forEach(item => {
                item.classList.remove('navbar__link--expand')
            })

            if (shouldClose) {
                header.classList.remove('menu-open')
            } else {
                header.classList.add('menu-open')
                e.target.parentNode.classList.add('navbar__link--expand')
            }
        }
    }

    const navbarItemClass = (link) => {
        let classes = [
            'navbar__item',
            `navbar--level-${link.overrideLevel || currentLevel}`,
            link.isMegamenu ? 'isMegamenu' : '',
            link && link.hasWidget ? 'navbar__item--widget' : ''
        ];

        return classes.join(' ');
    }

    const additionClass =
        contentLink && contentLink.attributes
            ? contentLink.attributes.cssValue
            : null
    const hasChildrenClass = links.length > 0 ? 'has-children' : null;
    const showTopLevelIcons = currentLevel === 1 && links.length > 0 && !isMuurikka;
    const enableRowWrapper = currentLevel === 1;

    return (
        <>
            {!contentLink ?
                <a
                    ref={burgerBtn}
                    className="navbar__link--block navbar__icon--menu navbar__icon"
                    onClick={toggleMenu}
                />
                :
                <>
                    {!showWidget && <MenuItem {...{
                        contentLink,
                        links,
                        showTopLevelIcons,
                        hasChildrenClass,
                        additionClass,
                        megaMenuLink,
                        linkHover,
                        toggleMenu
                    }} />}
                </>
            }

            {!showWidget && links.length > 0 &&
                <div className={'navbar__menu navbar--level-' + currentLevel} ref={menuRef}>
                    {enableRowWrapper ?
                        <div className="row"><MenuSectionContent /></div> :
                        <MenuSectionContent />
                    }
                </div>
            }

            {showWidget && <WidgetItem widget={contentLink.widgetItem} />}
        </>
    )
}

export default Navigation
